import { motion, AnimatePresence } from 'framer-motion';
import {
    Calendar,
    MapPin,
    Users,
    X,
    Save,
    Trash2,
    AlertTriangle,
    Type,
} from 'lucide-react';
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

const modalVariants = {
  hidden: {
    opacity: 0,
    scale: 0.9,
    y: 20
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 25
    }
  },
  exit: {
    opacity: 0,
    scale: 0.9,
    y: 20,
    transition: {
      duration: 0.2
    }
  }
};

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 }
};

const CalendarEventModal = ({
  isOpen,
  onClose,
  event,
  date,
  employees,
  onSave,
  onDelete
}) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    endTime: '',
    type: 'meeting',
    taggedEmployees: [],
    location: '',
    color: 'primary'
  });

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (event) {
      setFormData({
        title: event.title || '',
        description: event.description || '',
        date: event.date || '',
        time: event.time || '',
        endTime: event.endTime || '',
        type: event.type || 'meeting',
        taggedEmployees: event.taggedEmployees || [],
        location: event.location || '',
        color: event.color || 'primary'
      });
    } else if (date) {
      setFormData(prev => ({
        ...prev,
        date: date,
        time: '09:00',
        endTime: '10:00'
      }));
    }
  }, [event, date]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleEmployeeToggle = (employeeId) => {
    setFormData(prev => ({
      ...prev,
      taggedEmployees: prev.taggedEmployees.includes(employeeId)
        ? prev.taggedEmployees.filter(id => id !== employeeId)
        : [...prev.taggedEmployees, employeeId]
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.date) {
      newErrors.date = 'Date is required';
    }

    if (!formData.time) {
      newErrors.time = 'Start time is required';
    }

    if (!formData.endTime) {
      newErrors.endTime = 'End time is required';
    }

    if (formData.time && formData.endTime && formData.time >= formData.endTime) {
      newErrors.endTime = 'End time must be after start time';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    onSave(formData);
    toast.success(event ? 'Event updated successfully' : 'Event created successfully');
  };

  const handleDelete = () => {
    if (event) {
      onDelete(event.id);
      toast.success('Event deleted successfully');
    }
  };

  const eventTypes = [
    { value: 'meeting', label: 'Meeting', icon: Users, color: 'bg-primary-100 text-primary-700' },
    { value: 'task', label: 'Task', icon: Type, color: 'bg-secondary-100 text-secondary-700' },
    { value: 'reminder', label: 'Reminder', icon: AlertTriangle, color: 'bg-accent-100 text-accent-700' },
    { value: 'event', label: 'Event', icon: Calendar, color: 'bg-warning-100 text-warning-700' }
  ];

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[60] flex items-center justify-center p-4"
        variants={overlayVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
      >
        {/* Overlay */}
        <motion.div
          className="absolute inset-0 bg-black bg-opacity-50"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          className="relative bg-white rounded-xl shadow-intense max-w-2xl w-full max-h-[90vh] overflow-hidden"
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="subsection-title">
              {event ? 'Edit Event' : 'Create New Event'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            <div className="space-y-6">
              {/* Title */}
              <div>
                <label className="form-label">Event Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className={`input-field ${errors.title ? 'border-secondary-500 focus:border-secondary-500' : ''}`}
                  placeholder="Enter event title"
                />
                {errors.title && (
                  <p className="text-secondary-600 text-sm mt-1">{errors.title}</p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="form-label">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="input-field h-24 resize-none"
                  placeholder="Enter event description"
                  rows={3}
                />
              </div>

              {/* Type */}
              <div>
                <label className="form-label">Event Type</label>
                <div className="grid grid-cols-2 gap-3">
                  {eventTypes.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => handleInputChange('type', type.value)}
                      className={`flex items-center p-3 rounded-lg border-2 transition-all ${
                        formData.type === type.value
                          ? 'border-primary-300 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className={`p-2 rounded-lg mr-3 ${type.color}`}>
                        <type.icon size={16} />
                      </div>
                      <span className="font-medium text-sm">{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Date and Time */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="form-label">Date *</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => handleInputChange('date', e.target.value)}
                    className={`input-field ${errors.date ? 'border-secondary-500 focus:border-secondary-500' : ''}`}
                  />
                  {errors.date && (
                    <p className="text-secondary-600 text-sm mt-1">{errors.date}</p>
                  )}
                </div>

                <div>
                  <label className="form-label">Start Time *</label>
                  <input
                    type="time"
                    value={formData.time}
                    onChange={(e) => handleInputChange('time', e.target.value)}
                    className={`input-field ${errors.time ? 'border-secondary-500 focus:border-secondary-500' : ''}`}
                  />
                  {errors.time && (
                    <p className="text-secondary-600 text-sm mt-1">{errors.time}</p>
                  )}
                </div>

                <div>
                  <label className="form-label">End Time *</label>
                  <input
                    type="time"
                    value={formData.endTime}
                    onChange={(e) => handleInputChange('endTime', e.target.value)}
                    className={`input-field ${errors.endTime ? 'border-secondary-500 focus:border-secondary-500' : ''}`}
                  />
                  {errors.endTime && (
                    <p className="text-secondary-600 text-sm mt-1">{errors.endTime}</p>
                  )}
                </div>
              </div>

              {/* Location */}
              <div>
                <label className="form-label">Location</label>
                <div className="relative">
                  <MapPin size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    className="input-field pl-10"
                    placeholder="Enter location or meeting link"
                  />
                </div>
              </div>

              {/* Tagged Employees */}
              <div>
                <label className="form-label">Tag Employees</label>
                <div className="border border-gray-200 rounded-lg p-4 max-h-48 overflow-y-auto">
                  <div className="space-y-2">
                    {employees.map((employee) => (
                      <div
                        key={employee.id}
                        className={`flex items-center p-2 rounded-lg cursor-pointer transition-colors ${
                          formData.taggedEmployees.includes(employee.id)
                            ? 'bg-primary-50 border border-primary-200'
                            : 'hover:bg-gray-50'
                        }`}
                        onClick={() => handleEmployeeToggle(employee.id)}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium mr-3 ${
                          formData.taggedEmployees.includes(employee.id)
                            ? 'bg-primary-100 text-primary-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {employee.avatar}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{employee.name}</p>
                          <p className="text-xs text-gray-500">{employee.position}</p>
                        </div>
                        {formData.taggedEmployees.includes(employee.id) && (
                          <div className="w-4 h-4 bg-primary-600 rounded-full flex items-center justify-center">
                            <div className="w-2 h-2 bg-white rounded-full" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                {formData.taggedEmployees.length > 0 && (
                  <p className="text-sm text-gray-600 mt-2">
                    {formData.taggedEmployees.length} employee{formData.taggedEmployees.length > 1 ? 's' : ''} tagged
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-gray-200">
            <div>
              {event && (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="text-secondary-600 hover:text-secondary-700 flex items-center text-sm font-medium"
                >
                  <Trash2 size={16} className="mr-1" />
                  Delete Event
                </button>
              )}
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="btn-outline"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="btn-primary flex items-center"
              >
                <Save size={16} className="mr-2" />
                {event ? 'Update Event' : 'Create Event'}
              </button>
            </div>
          </div>
        </motion.div>

        {/* Delete Confirmation Modal */}
        <AnimatePresence>
          {showDeleteConfirm && (
            <motion.div
              className="absolute inset-0 z-[70] flex items-center justify-center"
              variants={overlayVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <motion.div
                className="bg-white rounded-xl shadow-intense p-6 max-w-md w-full mx-4"
                variants={modalVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <div className="text-center">
                  <div className="w-12 h-12 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertTriangle size={24} className="text-secondary-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Event</h3>
                  <p className="text-gray-600 mb-6">
                    Are you sure you want to delete this event? This action cannot be undone.
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      className="flex-1 btn-outline"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDelete}
                      className="flex-1 btn-danger"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
};

export default CalendarEventModal;
